import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../redux/hooks";
import { onSetBomsAdding, onSetSelectedBoms, workorderManagementBoms } from "../../../redux/slices/workorderManagement.feature";
import { DtGridPropsOptions2 } from "../../../types/dtGridPropsOptions2";
import { IRootState } from "../../../types/redux/IRootStore";
import { DtGrid2 } from "../../common/DtGrid2";
import { workorderManagementBomPaneColumns } from "./WorkorderManagementBomPaneColumns";
import { useTranslation } from "react-i18next";
import { bootboxConfirm } from "../../common/bootboxWrapper";
import AplSettingsSelector from "../../../redux/helpers/AplSettingsSelector";
import {
    addWorkorderManagementBom,
    deleteWorkorderManagementBom,
    saveWorkorderManagementBom
} from "../../../aplApi/workorderManagementHelper";
import { tryCatchBootbox } from "../../../aplApi/apiTryCatch";

export default function WorkorderManagementBomPane() {
    const { t } = useTranslation();
    const workorderManagementRedux = useSelector((state: IRootState) => state.workorderManagement);
    const dispatch = useAppDispatch();
    const aplSettings = AplSettingsSelector();

    const setSelected = (x: any) => {
        dispatch(onSetSelectedBoms(x));
        dispatch(onSetBomsAdding([]));
    };

    const defaultBom = {
        woBomId: Guid.createEmpty().toString(),
        workorderId: Guid.createEmpty().toString(),
        materialId: Guid.createEmpty().toString(),
        description: workorderManagementRedux.orderMaterialData?.description1,
        materialSapCode: `* ${workorderManagementRedux.orderMaterialData?.materialSapCode} *`,
        schedQuantity: 0,
        siteId: aplSettings.siteId,
        isActive: true,
        backFlush: true,
        editable: false
    };
    const dtGridOptns: DtGridPropsOptions2 = {
        data: [defaultBom, ...workorderManagementRedux.orderBomsData],
        columnData: workorderManagementBomPaneColumns(aplSettings.culture),
        defaultSort: {
            field: ["woBomId"],
            direction: ["asc"]
        },
        selectType: "single",
        colouring: false,
        headerFilters: false,
        setSelectedItems: setSelected,
        selectedItems: workorderManagementRedux.orderBomsSelected,
        tableName: "tblWorkorderBoms",
        tableClass: "table-workorder-boms",
        paging: false,
        showSpinner: false,
        rowId: "woBomId",
        pageLength: 20,
        loading: workorderManagementRedux.orderBomsDataLoading,
        bulk: false,
        filters: {},
        setFilters: _.noop,
        refreshSelectedOnDataChange: false,
        page: 1,
        setPage: _.noop
    };

    const refreshData = () => {
        if(workorderManagementRedux.orderData?.workorderId)
            dispatch(workorderManagementBoms(workorderManagementRedux.orderData?.workorderId) as any);
        else
            console.log(`${new Date().toISOString()} - WorkorderManagementBomPane.tsx - refreshData - workorderManagementRedux.orderData?.workorderId is null`);
    };

    const deleteBom = async () => {
        bootboxConfirm(
            t("main.delete")!,
            null,
            async (confirmed: boolean) => {
                if (confirmed) {
                    await tryCatchBootbox(async () => {
                        await deleteWorkorderManagementBom(workorderManagementRedux.orderBomsSelected[0]);
                    });
                    refreshData();
                }
            },
            null,
            aplSettings.culture
        );
    };

    const saveBom = async () => {
        await tryCatchBootbox(async () => {
            await saveWorkorderManagementBom(workorderManagementRedux.orderBomsSelected[0]);
        });
        refreshData();
    };

    const addBom = async () => {
        await tryCatchBootbox(async () => {
            await addWorkorderManagementBom(workorderManagementRedux.orderBomsAdding[0]);
        });
        refreshData();
    };

    const insertBom = () => {
        dispatch(
            onSetBomsAdding([
                {
                    woBomId: Guid.createEmpty().toString(),
                    workorderId: workorderManagementRedux.orderData?.workorderId.toString(),
                    materialId: Guid.createEmpty().toString(),
                    description: "",
                    materialSapCode: "",
                    schedQuantity: 0,
                    siteId: aplSettings.siteId,
                    isActive: true,
                    backFlush: false
                }
            ])
        );
    };

    return (
        <React.Fragment>
            <div className="container mw-100 p-2 pt-0 mt-1">
                <div className="row ml-1 mr-1">
                    <div className="col-sm-12">
                        <DtGrid2 options={dtGridOptns} />
                    </div>
                </div>
                {workorderManagementRedux.orderBomsAdding.length === 0 ? (
                    <div className="row ml-1 mr-1">
                        <div className="col-sm-10">&nbsp;</div>
                        <div className="col-sm-2 mt-1">
                            <input
                                type="button"
                                className="btn btn-primary float-right ml-1 mr-1"
                                onClick={e => {
                                    dispatch(onSetSelectedBoms([]));
                                    insertBom();
                                }}
                                value={t("main.insert")!}
                            />
                        </div>
                    </div>
                ) : null}
            </div>

            {workorderManagementRedux.orderBomsSelected.length === 1 && (workorderManagementRedux.orderBomsSelected[0].editable ?? true) ? (
                <div className="container mw-100 p-2 pt-0 mt-1" id="wrkBomEdit">
                    <div className="row ml-1 mr-1">
                        <div className="col hide">
                            <input
                                type="text"
                                className="form-control hide"
                                id="txtBomId"
                                value={workorderManagementRedux.orderBomsSelected[0].woBomId}
                                onChange={() => {}}
                            />
                        </div>
                        <div className="col-sm-3">
                            <input
                                type="text"
                                className="form-control"
                                readOnly={true}
                                id="txtBomComponent"
                                value={workorderManagementRedux.orderBomsSelected[0].materialSapCode}
                                onChange={() => {}}
                            />
                        </div>
                        <div className="col-sm-3">
                            <input
                                type="text"
                                className="form-control"
                                readOnly={true}
                                id="txtBomDescription"
                                value={workorderManagementRedux.orderBomsSelected[0].description}
                                onChange={() => {}}
                            />
                        </div>
                        <div className="col-sm-2 pt-1">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="chkBomMassIssue"
                                onChange={e => {
                                    dispatch(
                                        onSetSelectedBoms([
                                            {
                                                ...workorderManagementRedux.orderBomsSelected[0],
                                                backFlush: e.currentTarget.checked
                                            }
                                        ])
                                    );
                                }}
                                checked={workorderManagementRedux.orderBomsSelected[0].backFlush}
                            />
                            <label className="form-check-label" htmlFor="chkInLine">
                                {t("main.massIssue")!}
                            </label>
                        </div>
                        <div className="col-sm-2 pt-1">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="chkBomIsActive"
                                onChange={e => {
                                    dispatch(
                                        onSetSelectedBoms([
                                            {
                                                ...workorderManagementRedux.orderBomsSelected[0],
                                                isActive: e.currentTarget.checked
                                            }
                                        ])
                                    );
                                }}
                                checked={workorderManagementRedux.orderBomsSelected[0].isActive}
                            />
                            <label className="form-check-label" htmlFor="chkInLine">
                                {t("main.isActive")!}
                            </label>
                        </div>
                        <div className="col-sm-2">
                            <input
                                type="button"
                                className="btn btn-primary float-right"
                                onClick={e => {
                                    saveBom();
                                }}
                                value={t("main.save")!}
                            />
                            <input
                                type="button"
                                className="btn btn-danger float-right ml-1 mr-1"
                                onClick={e => {
                                    deleteBom();
                                }}
                                value={t("main.delete")!}
                            />
                        </div>
                    </div>
                </div>
            ) : null}

            {workorderManagementRedux.orderBomsAdding.length === 1 ? (
                <div className="container mw-100 p-2 pt-0 mt-1" id="wrkBomAdd">
                    <div className="row ml-1 mr-1">
                        <div className="col hide">
                            <input type="text" className="form-control hide" id="txtBomIdAdd" />
                        </div>
                        <div className="col-sm-3">
                            <input
                                type="text"
                                className="form-control"
                                id="txtBomComponentAdd"
                                onChange={e => {
                                    dispatch(
                                        onSetBomsAdding([
                                            {
                                                ...workorderManagementRedux.orderBomsAdding[0],
                                                materialSapCode: e.currentTarget.value.trim()
                                            }
                                        ])
                                    );
                                }}
                                value={workorderManagementRedux.orderBomsAdding[0].materialSapCode}
                            />
                        </div>
                        <div className="col-sm-3">
                            <input type="text" className="form-control" readOnly={true} id="txtBomDescriptionAdd" />
                        </div>
                        <div className="col-sm-2 pt-1">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="chkBomMassIssueAdd"
                                onChange={e => {
                                    dispatch(
                                        onSetBomsAdding([
                                            {
                                                ...workorderManagementRedux.orderBomsAdding[0],
                                                backFlush: e.currentTarget.checked
                                            }
                                        ])
                                    );
                                }}
                                checked={workorderManagementRedux.orderBomsAdding[0].backFlush}
                            />
                            <label className="form-check-label" htmlFor="chkInLine">
                                {t("main.massIssue")!}
                            </label>
                        </div>
                        <div className="col-sm-2 pt-1">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="chkBomIsActiveAdd"
                                onChange={e => {
                                    dispatch(
                                        onSetBomsAdding([
                                            {
                                                ...workorderManagementRedux.orderBomsAdding[0],
                                                isActive: e.currentTarget.checked
                                            }
                                        ])
                                    );
                                }}
                                checked={workorderManagementRedux.orderBomsAdding[0].isActive}
                            />
                            <label className="form-check-label" htmlFor="chkInLine">
                                {t("main.isActive")!}
                            </label>
                        </div>
                        <div className="col-sm-2">
                            <input
                                type="button"
                                className="btn btn-primary float-right"
                                onClick={e => {
                                    addBom();
                                }}
                                value={t("main.save")!}
                            />

                            <input
                                type="button"
                                className="btn btn-danger float-right mr-1"
                                onClick={e => {
                                    dispatch(onSetBomsAdding([]));
                                }}
                                value={t("main.cancel")!}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </React.Fragment>
    );
}
